import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiSave,
  FiBox,
  FiDollarSign,
  FiClock,
  FiImage,
  FiUploadCloud,
  FiCheck,
  FiChevronRight,
  FiChevronLeft,
  FiLayers,
  FiTrash2,
  FiPlus,
  FiMapPin,
  FiX,
  FiCrop,
  FiSearch,
} from "react-icons/fi";
import axiosInstance from "../config/axiosInstance";
import RichTextEditor from "../components/RichTextEditor";
import BreadcrumbTrail from "../components/BreadcrumbTrail";
import ImageCropModal from "../components/modals/ImageCropModal";
import { readFileAsDataUrl } from "../utils/image";
import { useCompany } from "../hooks/useCompany";


const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/jpg",
];

const NewProductPage = () => {
  const navigate = useNavigate();
  const { id: productId } = useParams();
  const { user } = useAuth();
  const { loading: companyLoading } = useCompany();
  const isEditing = Boolean(productId);
  const [loading, setLoading] = useState(false);
  const [productLoading, setProductLoading] = useState(false);
  const [error, setError] = useState("");
  const [planLimit, setPlanLimit] = useState(null);
  // Fotos por anúncio liberadas pelo plano (1 no Gratuito, 3 no Pro, 5 no Premium).
  const [photosLimit, setPhotosLimit] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [mediaPreviewUrls, setMediaPreviewUrls] = useState({});
  const [cropSource, setCropSource] = useState(null);
  const [isDraggingPhoto, setIsDraggingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  // Wizard state
  const [step, setStep] = useState(1);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    categoryId: "",
    description: "",
    coverageStates: [],
    coverageCities: [],
    priceCondition: "",
    hasUrgency: false,
    media: [],
  });

  const [ibgeStates, setIbgeStates] = useState([]);
  const [ibgeStatesLoading, setIbgeStatesLoading] = useState(false);
  const [ibgeCitiesCache, setIbgeCitiesCache] = useState({});
  const [ibgeCitiesLoading, setIbgeCitiesLoading] = useState({});
  const [stateSearch, setStateSearch] = useState("");
  const [citySearches, setCitySearches] = useState({});
  const [coverageCityMode, setCoverageCityMode] = useState({});
  const [cityDropdownOpen, setCityDropdownOpen] = useState({});

  useEffect(() => {
    const fetchPhotosLimit = async () => {
      try {
        const response = await axiosInstance.get("/billing/me");
        const limit =
          response.data?.data?.entitlements?.limits?.photosPerProduct;
        if (Number(limit) > 0) setPhotosLimit(Number(limit));
      } catch (fetchError) {
        // Sem o limite em mãos o backend ainda barra o excedente no envio.
        setPhotosLimit(null);
      }
    };

    fetchPhotosLimit();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      setCategoriesLoading(true);
      try {
        const response = await axiosInstance.get("/categories", {
          params: { grouped: true, isActive: true },
        });
        setCategories(response.data?.data?.data || response.data?.data || []);
      } catch (fetchError) {
        setCategories([]);
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchStates = async () => {
      setIbgeStatesLoading(true);
      try {
        const res = await fetch(
          "https://servicodados.ibge.gov.br/api/v1/localidades/estados?orderBy=nome"
        );
        const data = await res.json();
        setIbgeStates(data);
      } catch {
        setIbgeStates([]);
      } finally {
        setIbgeStatesLoading(false);
      }
    };
    fetchStates();
  }, []);

  useEffect(() => {
    if (!isEditing || !productId) return;

    const fetchProduct = async () => {
      setProductLoading(true);
      setError("");

      try {
        const response = await axiosInstance.get(`/products/${productId}`);
        const product = response?.data?.data || response?.data;
        const media = Array.isArray(product?.media) ? product.media : [];
        const mediaUrls = Array.isArray(product?.mediaUrls)
          ? product.mediaUrls
          : [];

        setMediaPreviewUrls(
          media.reduce((accumulator, mediaItem, index) => {
            const mediaUrl = mediaUrls[index];

            if (!mediaUrl) return accumulator;

            accumulator[mediaItem] = mediaUrl;
            return accumulator;
          }, {}),
        );

        const loadedStates = Array.isArray(product?.coverageStates) ? product.coverageStates : [];
        const loadedCities = Array.isArray(product?.coverageCities) ? product.coverageCities : [];

        setFormData({
          title: product?.title || "",
          categoryId: product?.categoryId ? String(product.categoryId) : "",
          description: product?.description || "",
          coverageStates: loadedStates,
          coverageCities: loadedCities,
          priceCondition: product?.priceCondition || "",
          hasUrgency: Boolean(product?.hasUrgency),
          media,
        });

        // Busca cidades de cada estado salvo e detecta modo
        for (const sigla of loadedStates) {
          setIbgeCitiesLoading((prev) => ({ ...prev, [sigla]: true }));
          fetch(
            `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${sigla}/municipios?orderBy=nome`
          )
            .then((r) => r.json())
            .then((data) => {
              setIbgeCitiesCache((prev) => ({ ...prev, [sigla]: data }));
              const hasSpecific = data.some((c) => loadedCities.includes(c.nome));
              setCoverageCityMode((prev) => ({
                ...prev,
                [sigla]: hasSpecific ? "specific" : "all",
              }));
            })
            .catch(() => setIbgeCitiesCache((prev) => ({ ...prev, [sigla]: [] })))
            .finally(() =>
              setIbgeCitiesLoading((prev) => ({ ...prev, [sigla]: false }))
            );
        }
      } catch (fetchError) {
        setError("Não foi possível carregar o produto para edição.");
      } finally {
        setProductLoading(false);
      }
    };

    fetchProduct();
  }, [isEditing, productId]);

  useEffect(() => {
    return () => {
      Object.values(mediaPreviewUrls).forEach((previewUrl) => {
        if (typeof previewUrl === "string" && previewUrl.startsWith("blob:")) {
          URL.revokeObjectURL(previewUrl);
        }
      });
    };
  }, [mediaPreviewUrls]);

  const selectedCategoryName = useMemo(() => {
    for (const parent of categories) {
      if (String(parent.id) === String(formData.categoryId)) {
        return parent.nome;
      }
      const sub = parent.subcategories?.find(
        (s) => String(s.id) === String(formData.categoryId)
      );
      if (sub) return `${parent.nome} › ${sub.nome}`;
    }
    return "Não selecionada";
  }, [categories, formData.categoryId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const fetchCitiesForState = async (sigla) => {
    if (ibgeCitiesCache[sigla] !== undefined) return;
    setIbgeCitiesLoading((prev) => ({ ...prev, [sigla]: true }));
    try {
      const res = await fetch(
        `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${sigla}/municipios?orderBy=nome`
      );
      const data = await res.json();
      setIbgeCitiesCache((prev) => ({ ...prev, [sigla]: data }));
    } catch {
      setIbgeCitiesCache((prev) => ({ ...prev, [sigla]: [] }));
    } finally {
      setIbgeCitiesLoading((prev) => ({ ...prev, [sigla]: false }));
    }
  };

  const toggleStateSelection = (sigla) => {
    const isSelected = formData.coverageStates.includes(sigla);
    if (isSelected) {
      const stateCityNames = (ibgeCitiesCache[sigla] || []).map((c) => c.nome);
      setFormData((prev) => ({
        ...prev,
        coverageStates: prev.coverageStates.filter((s) => s !== sigla),
        coverageCities: prev.coverageCities.filter((c) => !stateCityNames.includes(c)),
      }));
      setCoverageCityMode((prev) => { const next = { ...prev }; delete next[sigla]; return next; });
      setCitySearches((prev) => { const next = { ...prev }; delete next[sigla]; return next; });
    } else {
      setFormData((prev) => ({
        ...prev,
        coverageStates: [...prev.coverageStates, sigla],
      }));
      setCoverageCityMode((prev) => ({ ...prev, [sigla]: "all" }));
      fetchCitiesForState(sigla);
    }
  };

  const toggleCitySelection = (cityName) => {
    setFormData((prev) => ({
      ...prev,
      coverageCities: prev.coverageCities.includes(cityName)
        ? prev.coverageCities.filter((c) => c !== cityName)
        : [...prev.coverageCities, cityName],
    }));
  };

  const handlePriceChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value) {
      value = (parseInt(value, 10) / 100).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    }
    setFormData((prev) => ({
      ...prev,
      priceCondition: value,
    }));
  };

  const uploadFile = async (file) => {
    setUploading(true);
    const data = new FormData();
    data.append("image", file);

    try {
      const response = await axiosInstance.post("/upload/product", data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      const imageUrl =
        response?.data?.imageUrl || response?.data?.imageName || "";

      if (imageUrl) {
        const previewUrl = URL.createObjectURL(file);

        setFormData((prev) => ({
          ...prev,
          media: [...prev.media, imageUrl],
        }));
        setMediaPreviewUrls((prev) => ({
          ...prev,
          [imageUrl]: previewUrl,
        }));
      } else {
        throw new Error("Resposta de upload sem URL da imagem");
      }
    } catch (err) {
      console.error("Erro no upload, usando base64 fallback", err);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          media: [...prev.media, reader.result],
        }));
      };
      reader.readAsDataURL(file);
    } finally {
      setUploading(false);
    }
  };

  const canAddPhoto =
    photosLimit === null || formData.media.length < photosLimit;

  // Abre o ajuste da foto. O recorte é opcional: "Usar original" envia o
  // arquivo exatamente como veio, sem reamostrar.
  const prepareFile = async (file) => {
    if (!file) return;

    if (!canAddPhoto) {
      setError(
        `Seu plano permite ${photosLimit} foto${photosLimit > 1 ? "s" : ""} por anúncio. Faça upgrade para publicar mais.`,
      );
      return;
    }

    const extension = file.name.split(".").pop()?.toLowerCase();
    const hasValidExtension = ["jpg", "jpeg", "png", "webp"].includes(extension);
    const hasValidMimeType = ALLOWED_IMAGE_TYPES.includes(file.type);

    if (!hasValidExtension || !hasValidMimeType) {
      setError("Formato inválido. Envie apenas imagens JPG, JPEG, PNG ou WEBP.");
      return;
    }

    setError("");

    try {
      const dataUrl = await readFileAsDataUrl(file);
      setCropSource({
        file,
        dataUrl,
        name: file.name.replace(/\.[^/.]+$/, "") || "produto",
        type:
          file.type === "image/png"
            ? "image/png"
            : file.type === "image/webp"
              ? "image/webp"
              : "image/jpeg",
      });
    } catch (readError) {
      // Sem preview possível, envia o arquivo original mesmo assim.
      uploadFile(file);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    prepareFile(file);
  };

  const handlePhotoDrop = (event) => {
    event.preventDefault();
    setIsDraggingPhoto(false);
    prepareFile(event.dataTransfer?.files?.[0]);
  };

  const closeCropper = () => {
    if (uploading) return;
    setCropSource(null);
  };

  const confirmCrop = async (croppedFile) => {
    await uploadFile(croppedFile);
    setCropSource(null);
  };

  const useOriginalPhoto = async () => {
    if (!cropSource?.file) return;
    await uploadFile(cropSource.file);
    setCropSource(null);
  };

  const removeMedia = (index) => {
    setFormData((prev) => {
      const mediaItem = prev.media[index];

      setMediaPreviewUrls((currentPreviewUrls) => {
        const previewUrl = currentPreviewUrls[mediaItem];

        if (previewUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(previewUrl);
        }

        return Object.fromEntries(
          Object.entries(currentPreviewUrls).filter(([key]) => key !== mediaItem),
        );
      });

      return {
        ...prev,
        media: prev.media.filter((_, i) => i !== index),
      };
    });
  };

  const resolveMediaPreviewSrc = (mediaItem) => {
    if (!mediaItem) return "";
    if (typeof mediaItem !== "string") return "";
    if (
      mediaItem.startsWith("data:") ||
      mediaItem.startsWith("blob:") ||
      mediaItem.startsWith("http://") ||
      mediaItem.startsWith("https://")
    ) {
      return mediaItem;
    }

    return mediaPreviewUrls[mediaItem] || `https://relinq.s3.sa-east-1.amazonaws.com/${mediaItem}`;
  };

  const handleSubmit = async () => {
    if (!formData.categoryId) {
      setError("Selecione a categoria do produto para continuar.");
      setStep(1);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const payload = {
        title: formData.title,
        categoryId: formData.categoryId,
        description: formData.description,
        coverageStates: formData.coverageStates,
        coverageCities: formData.coverageCities,
        priceCondition: formData.priceCondition,
        hasUrgency: formData.hasUrgency,
        media: formData.media,
      };

      if (isEditing) {
        await axiosInstance.put(`/products/${productId}`, payload);
      } else {
        await axiosInstance.post("/products", payload);
      }

      navigate("/produtos");
    } catch (err) {
      console.error(err);

      // 402 = limite do plano atingido; leva o anunciante direto ao upgrade.
      if (err?.response?.data?.code === "PHOTO_LIMIT_REACHED") {
        const limit = err.response.data.data?.photosLimit;
        if (Number(limit) > 0) setPhotosLimit(Number(limit));
        setError(err.response.data.message);
        setStep(1);
        setLoading(false);
        return;
      }

      if (err?.response?.data?.code === "PLAN_LIMIT_REACHED") {
        setPlanLimit(err.response.data.data || {});
        setError(err.response.data.message);
        setLoading(false);
        return;
      }

      setError(
        err?.response?.data?.message ||
          (isEditing
            ? "Ocorreu um erro ao atualizar o produto. Verifique os dados e tente novamente."
            : "Ocorreu um erro ao submeter o catálogo. Verifique os dados e tente novamente."),
      );
    } finally {
      setLoading(false);  
    }
  };

  const clearCoverage = () => {
    setFormData((prev) => ({ ...prev, coverageStates: [], coverageCities: [] }));
    setCoverageCityMode({});
    setCitySearches({});
  };

  const nextStep = () => setStep((prev) => Math.min(prev + 1, 3));
  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  const isStep1Valid =
    formData.title.trim().length > 0 && String(formData.categoryId).trim().length > 0;

  // Steps visual configuration
  const steps = [
    { num: 1, label: "Conteúdo" },
    { num: 2, label: "Condições" },
    { num: 3, label: "Revisão Final" },
  ];

  const breadcrumbItems = [
    { label: "Produtos", to: user?.role === "ADMIN" ? "/admin/produtos" : "/produtos" },
    { label: isEditing ? "Editar produto" : "Novo produto" },
  ];

  if (companyLoading || productLoading) {
    return <div className="muted">Carregando dados da empresa...</div>;
  }

  return (
    <div className="fade-in page-stack">
      <div className="page-header" style={{ alignItems: "center" }}>
        <div style={{ width: "100%" }}>
          <BreadcrumbTrail items={breadcrumbItems} />
        </div>
      </div>

      <div className="card card--outlined w-full" style={{ margin: "0 auto" }}>
        {/* Passos do cadastro */}
        <div className="wizard-steps">
          {steps.map((item, index) => {
            const isActive = step === item.num;
            const isDone = step > item.num;

            return (
              <div
                key={item.num}
                style={{
                  display: "flex",
                  alignItems: "center",
                  flex: index < steps.length - 1 ? 1 : "none",
                  minWidth: 0,
                }}
              >
                <button
                  type="button"
                  className={`wizard-step${isActive ? " is-active" : ""}${
                    isDone ? " is-done" : ""
                  }`}
                  onClick={() => {
                    // Só deixa voltar: avançar continua passando pela validação.
                    if (item.num < step) setStep(item.num);
                  }}
                  disabled={item.num > step}
                >
                  <span className="wizard-step__index">
                    {isDone ? <FiCheck size={12} /> : item.num}
                  </span>
                  {item.label}
                </button>

                {index < steps.length - 1 && (
                  <span className={`wizard-line${isDone ? " is-done" : ""}`} />
                )}
              </div>
            );
          })}
        </div>

        {planLimit ? (
          /* Limite do plano atingido: converte o erro num convite de upgrade. */
          <div
            style={{
              border: "1.5px solid #fed7aa",
              background: "linear-gradient(180deg,#fff7ed 0%,#ffffff 100%)",
              padding: "1.15rem 1.25rem",
              borderRadius: "0.85rem",
              marginBottom: "1.5rem",
            }}
          >
            <strong style={{ display: "block", color: "#9a3412", fontSize: "0.95rem" }}>
              Limite do seu plano atingido
            </strong>
            <p style={{ margin: "0.4rem 0 0.9rem", color: "#7c2d12", fontSize: "0.875rem", lineHeight: 1.55 }}>
              Você já publicou {planLimit.productsUsed} de {planLimit.productsLimit} produtos
              disponíveis. Faça upgrade para continuar cadastrando.
            </p>
            <button
              type="button"
              className="button button--primary"
              onClick={() => navigate("/assinatura")}
            >
              Ver planos
            </button>
          </div>
        ) : (
          error && (
            <div
              style={{
                backgroundColor: "var(--color-danger-light, #fee2e2)",
                color: "var(--color-danger, #ef4444)",
                padding: "1rem",
                borderRadius: "0.5rem",
                marginBottom: "1.5rem",
                fontWeight: "500",
              }}
            >
              {error}
            </div>
          )
        )}

        <div style={{ minHeight: "350px", position: "relative" }}>
          {/* Sessão 1: Informações Gerais e Mídia */}
          {step === 1 && (
            <div className="fade-in">
              <h3 className="wizard-heading">
                <span className="wizard-heading__icon">
                  <FiBox size={18} />
                </span>
                Que lote quer cadastrar?
              </h3>
              <p className="wizard-subheading">Título, categoria, descrição e fotos do lote que vai para a vitrine.</p>

              <div
                className="form-group"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1.5rem",
                }}
              >
                {/* Fotos do produto */}
                <div>
                  <div className="photo-section__header">
                    <h4 className="photo-section__title">
                      <FiImage size={16} /> Fotos do produto
                    </h4>
                    <span className="photo-section__counter">
                      {photosLimit !== null
                        ? `${formData.media.length} de ${photosLimit} do seu plano`
                        : `${formData.media.length} foto(s)`}
                    </span>
                  </div>

                  <div className="photo-grid">
                    {formData.media.map((img, idx) => (
                      <div key={`${img}-${idx}`} className="photo-card">
                        <img src={resolveMediaPreviewSrc(img)} alt={`Foto ${idx + 1}`} />
                        <button
                          type="button"
                          className="photo-card__remove"
                          onClick={() => removeMedia(idx)}
                          aria-label={`Remover foto ${idx + 1}`}
                          title="Remover foto"
                        >
                          <FiTrash2 size={13} />
                        </button>
                        {idx === 0 && <span className="photo-card__cover">Capa</span>}
                      </div>
                    ))}

                    {!canAddPhoto ? (
                      /* Limite de fotos do plano atingido: vira convite de upgrade. */
                      <div className="photo-locked">
                        <span>
                          Limite de {photosLimit} foto{photosLimit > 1 ? "s" : ""} do seu
                          plano
                        </span>
                        <button type="button" onClick={() => navigate("/assinatura")}>
                          Ver planos
                        </button>
                      </div>
                    ) : (
                      <div
                        className={`photo-drop${isDraggingPhoto ? " is-dragging" : ""}${
                          uploading ? " is-disabled" : ""
                        }`}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          if (!uploading) fileInputRef.current?.click();
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            if (!uploading) fileInputRef.current?.click();
                          }
                        }}
                        onDragOver={(event) => {
                          event.preventDefault();
                          if (!uploading) setIsDraggingPhoto(true);
                        }}
                        onDragLeave={() => setIsDraggingPhoto(false)}
                        onDrop={handlePhotoDrop}
                      >
                        {uploading ? <FiUploadCloud size={22} /> : <FiPlus size={22} />}
                        {uploading ? "Enviando..." : "Adicionar foto"}
                        {!uploading && <small>ou arraste aqui</small>}
                      </div>
                    )}
                  </div>

                  <p
                    className="muted"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      margin: "10px 0 0",
                      fontSize: "0.79rem",
                    }}
                  >
                    <FiCrop size={13} />
                    A primeira foto vira a capa. Ao enviar você pode enquadrar a imagem —
                    ou usar a foto original, sem recorte.
                  </p>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                    style={{ display: "none" }}
                    onChange={handleFileUpload}
                    disabled={uploading}
                  />
                </div>

                <div className="pform-field">
                  <label className="pform-field__label" htmlFor="title">
                    <span>
                      Título do produto <em>*</em>
                    </span>
                    <span className="pform-field__counter">
                      {formData.title.length}/120
                    </span>
                  </label>
                  <input
                    id="title"
                    name="title"
                    type="text"
                    maxLength={120}
                    placeholder="Ex.: Lote de café arábica peneira 17, safra 2026"
                    className="input"
                    value={formData.title}
                    onChange={handleChange}
                    autoFocus
                    required
                  />
                  <p className="pform-field__hint">
                    É o que o comprador lê primeiro na vitrine — diga o produto, a
                    variedade e o tamanho do lote.
                  </p>
                </div>

                <div className="pform-field">
                  <label className="pform-field__label" htmlFor="categoryId">
                    <span>
                      Categoria do produto <em>*</em>
                    </span>
                  </label>
                  <select
                    id="categoryId"
                    name="categoryId"
                    className="select"
                    value={formData.categoryId}
                    onChange={handleChange}
                    required
                    disabled={categoriesLoading}
                  >
                    <option value="">
                      {categoriesLoading
                        ? "Carregando categorias..."
                        : "Selecione uma categoria"}
                    </option>
                    {categories.map((parent) =>
                      parent.subcategories?.length > 0 ? (
                        <optgroup key={parent.id} label={parent.nome}>
                          {parent.subcategories.map((sub) => (
                            <option key={sub.id} value={sub.id}>
                              {sub.nome}
                            </option>
                          ))}
                        </optgroup>
                      ) : (
                        <option key={parent.id} value={parent.id}>
                          {parent.nome}
                        </option>
                      )
                    )}
                  </select>
                  <p className="pform-field__hint">
                    Define em qual vitrine o produto aparece e quais compradores são
                    notificados.
                  </p>
                </div>

                <div className="pform-field">
                  <label className="pform-field__label" htmlFor="description">
                    <span>Descrição do produto</span>
                  </label>
                  <RichTextEditor
                    value={formData.description}
                    onChange={(val) =>
                      setFormData((prev) => ({ ...prev, description: val }))
                    }
                    placeholder="Origem, safra, qualidade, embalagem, pedido mínimo, prazo de entrega..."
                  />
                  <p className="pform-field__hint">
                    Anúncios com origem, pedido mínimo e prazo recebem mais cotações.
                  </p>
                </div>

                {/* Abrangência geográfica */}
                <div className="pform-block">
                  <div className="pform-block__head">
                    <h4 className="pform-block__title">
                      <FiMapPin size={15} /> Abrangência geográfica
                    </h4>
                    {formData.coverageStates.length > 0 && (
                      <button
                        type="button"
                        className="pform-ghost-button"
                        onClick={clearCoverage}
                      >
                        Limpar seleção ({formData.coverageStates.length})
                      </button>
                    )}
                  </div>
                  <p className="pform-field__hint">
                    Escolha os estados onde entrega e, se quiser, restrinja às cidades
                    que atende. Sem seleção, o produto aparece para o Brasil inteiro.
                  </p>

                  <div className="plist-search" style={{ marginTop: 12 }}>
                    <FiSearch className="plist-search__icon" size={16} />
                    <input
                      className="input"
                      placeholder="Buscar estado por nome ou sigla"
                      value={stateSearch}
                      onChange={(event) => setStateSearch(event.target.value)}
                    />
                    {stateSearch && (
                      <button
                        type="button"
                        className="plist-search__clear"
                        onClick={() => setStateSearch("")}
                        aria-label="Limpar busca de estado"
                      >
                        <FiX size={13} />
                      </button>
                    )}
                  </div>

                  {ibgeStatesLoading ? (
                    <p className="pform-field__hint" style={{ marginTop: 12 }}>
                      Carregando estados...
                    </p>
                  ) : (
                    <div className="pform-states">
                      {ibgeStates
                        .filter(
                          (item) =>
                            item.nome.toLowerCase().includes(stateSearch.toLowerCase()) ||
                            item.sigla.toLowerCase().includes(stateSearch.toLowerCase())
                        )
                        .map((state) => {
                          const selected = formData.coverageStates.includes(state.sigla);

                          return (
                            <button
                              key={state.id}
                              type="button"
                              className={`pform-state${selected ? " is-selected" : ""}`}
                              onClick={() => toggleStateSelection(state.sigla)}
                              aria-pressed={selected}
                            >
                              <span className="pform-state__uf">
                                {selected ? <FiCheck size={13} /> : state.sigla}
                              </span>
                              <span className="pform-state__name">{state.nome}</span>
                            </button>
                          );
                        })}
                    </div>
                  )}

                  {/* Seleção de cidades por estado */}
                  {formData.coverageStates.length > 0 && (
                    <div style={{ marginTop: "1.25rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
                      <h4 className="pform-block__title" style={{ margin: 0 }}>
                        Cidades por estado
                      </h4>
                      {formData.coverageStates.map((sigla) => {
                        const stateInfo = ibgeStates.find((s) => s.sigla === sigla);
                        const cities = ibgeCitiesCache[sigla] || [];
                        const isLoadingCities = ibgeCitiesLoading[sigla];
                        const mode = coverageCityMode[sigla] || "all";
                        const inputValue = citySearches[sigla] || "";
                        const isDropdownOpen = cityDropdownOpen[sigla] || false;
                        const selectedCities = formData.coverageCities.filter((c) =>
                          cities.some((city) => city.nome === c)
                        );
                        const suggestions = cities
                          .filter(
                            (c) =>
                              c.nome.toLowerCase().includes(inputValue.toLowerCase()) &&
                              !formData.coverageCities.includes(c.nome)
                          )
                          .slice(0, 8);

                        return (
                          <div key={sigla} className="pform-city-panel">
                            <div className="pform-city-panel__head">
                              <span className="pform-city-panel__title">
                                <span className="pform-state__uf">{sigla}</span>
                                {stateInfo?.nome || sigla}
                              </span>
                              <span className="pform-field__counter">
                                {mode === "all"
                                  ? "Todo o estado"
                                  : `${selectedCities.length} cidade${
                                      selectedCities.length === 1 ? "" : "s"
                                    }`}
                              </span>
                            </div>

                            <div className="pform-city-panel__body">
                              <div className="pform-segmented">
                                {[
                                  { value: "all", label: "Todas as cidades" },
                                  { value: "specific", label: "Cidades específicas" },
                                ].map((option) => (
                                  <button
                                    key={option.value}
                                    type="button"
                                    className={mode === option.value ? "is-active" : ""}
                                    onClick={() => {
                                      setCoverageCityMode((prev) => ({
                                        ...prev,
                                        [sigla]: option.value,
                                      }));
                                      if (option.value === "all") {
                                        const stateCityNames = cities.map((c) => c.nome);
                                        setFormData((prev) => ({
                                          ...prev,
                                          coverageCities: prev.coverageCities.filter(
                                            (c) => !stateCityNames.includes(c)
                                          ),
                                        }));
                                        setCitySearches((prev) => ({ ...prev, [sigla]: "" }));
                                      }
                                    }}
                                  >
                                    {option.label}
                                  </button>
                                ))}
                              </div>

                              {mode === "specific" &&
                                (isLoadingCities ? (
                                  <p className="pform-field__hint">Carregando cidades...</p>
                                ) : (
                                  <>
                                    <div style={{ position: "relative" }}>
                                      <input
                                        className="input"
                                        placeholder={`Buscar cidade em ${stateInfo?.nome || sigla}`}
                                        value={inputValue}
                                        autoComplete="off"
                                        onChange={(event) => {
                                          setCitySearches((prev) => ({
                                            ...prev,
                                            [sigla]: event.target.value,
                                          }));
                                          setCityDropdownOpen((prev) => ({
                                            ...prev,
                                            [sigla]: true,
                                          }));
                                        }}
                                        onFocus={() =>
                                          setCityDropdownOpen((prev) => ({
                                            ...prev,
                                            [sigla]: true,
                                          }))
                                        }
                                        onBlur={() =>
                                          setTimeout(
                                            () =>
                                              setCityDropdownOpen((prev) => ({
                                                ...prev,
                                                [sigla]: false,
                                              })),
                                            150
                                          )
                                        }
                                      />
                                      {isDropdownOpen &&
                                        inputValue.length > 0 &&
                                        suggestions.length > 0 && (
                                          <div className="pform-suggestions">
                                            {suggestions.map((city) => (
                                              <button
                                                key={city.id}
                                                type="button"
                                                onMouseDown={() => {
                                                  toggleCitySelection(city.nome);
                                                  setCitySearches((prev) => ({
                                                    ...prev,
                                                    [sigla]: "",
                                                  }));
                                                  setCityDropdownOpen((prev) => ({
                                                    ...prev,
                                                    [sigla]: false,
                                                  }));
                                                }}
                                              >
                                                {city.nome}
                                              </button>
                                            ))}
                                          </div>
                                        )}
                                    </div>

                                    {selectedCities.length > 0 ? (
                                      <div className="pform-chips">
                                        {selectedCities.map((cityName) => (
                                          <span key={cityName} className="pform-chip">
                                            {cityName}
                                            <button
                                              type="button"
                                              onClick={() => toggleCitySelection(cityName)}
                                              aria-label={`Remover ${cityName}`}
                                            >
                                              <FiX size={12} />
                                            </button>
                                          </span>
                                        ))}
                                      </div>
                                    ) : (
                                      <p className="pform-field__hint">
                                        Nenhuma cidade escolhida ainda — o produto vai
                                        aparecer para todo o estado.
                                      </p>
                                    )}
                                  </>
                                ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Sessão 2: Precificação e Urgência */}
          {step === 2 && (
            <div className="fade-in">
              <h3 className="wizard-heading">
                <span className="wizard-heading__icon">
                  <FiDollarSign size={18} />
                </span>
                Como quer negociar?
              </h3>
              <p className="wizard-subheading">Condição comercial, urgência e as regiões que você atende.</p>

              <div className="pform">
                <div className="pform-field">
                  <label className="pform-field__label" htmlFor="priceCondition">
                    <span>Preço ou condição comercial</span>
                  </label>
                  <div className="pform-money">
                    <span className="pform-money__prefix">R$</span>
                    <input
                      id="priceCondition"
                      name="priceCondition"
                      type="text"
                      inputMode="numeric"
                      placeholder="0,00"
                      className="input"
                      value={formData.priceCondition}
                      onChange={handlePriceChange}
                      autoFocus
                    />
                  </div>
                  <p className="pform-field__hint">
                    Digite apenas números — o valor é formatado sozinho. Deixe em branco
                    para o anúncio aparecer como <strong>“Sob consulta”</strong>.
                  </p>
                </div>

                <div className="pform-field">
                  <label className="pform-field__label">
                    <span>Destaque de urgência</span>
                  </label>

                  <label
                    htmlFor="hasUrgency"
                    className={`pform-toggle-card${formData.hasUrgency ? " is-on" : ""}`}
                  >
                    <input
                      id="hasUrgency"
                      name="hasUrgency"
                      type="checkbox"
                      checked={formData.hasUrgency}
                      onChange={handleChange}
                    />
                    <div>
                      <span className="pform-toggle-card__title">
                        <FiClock size={17} /> Validade próxima / lote de queima
                      </span>
                      <p className="pform-toggle-card__text">
                        O produto ganha o selo <strong>Laranja Alerta</strong> na vitrine e
                        aparece nas buscas por oportunidades — bom para girar estoque com
                        prazo curto.
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Sessão 3: Revisão Final */}
          {step === 3 && (
            <div className="fade-in">
              <h3 className="wizard-heading">
                <span className="wizard-heading__icon">
                  <FiLayers size={18} />
                </span>
                Revise seu catálogo
              </h3>
              <p className="wizard-subheading">Confira tudo antes de enviar o produto para aprovação da equipe.</p>

              <div className="pform-review">
                <div className="pform-review__preview">
                  <div className="pform-review__cover">
                    {formData.media.length > 0 ? (
                      <img
                        src={resolveMediaPreviewSrc(formData.media[0])}
                        alt="Foto de capa"
                      />
                    ) : (
                      <FiImage size={26} />
                    )}
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <h4 className="pform-review__title">
                      {formData.title || "Sem título"}
                    </h4>
                    <div
                      className="pform-review__desc"
                      dangerouslySetInnerHTML={{
                        __html: formData.description || "Sem descrição",
                      }}
                    />
                    <div className="pform-chips" style={{ marginTop: 10 }}>
                      <span className="plist-tag">{selectedCategoryName}</span>
                      <span className="plist-tag">
                        {formData.media.length} foto
                        {formData.media.length === 1 ? "" : "s"}
                      </span>
                      {formData.hasUrgency && (
                        <span className="plist-tag plist-tag--urgent">
                          <FiClock size={11} /> Laranja Alerta
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="pform-review__cards">
                  <div className="pform-review__card">
                    <span className="pform-review__label">
                      <FiDollarSign size={13} /> Preço / condição
                    </span>
                    <p className="pform-review__value">
                      {formData.priceCondition || "Sob consulta"}
                    </p>
                    <p className="pform-review__note">
                      Categoria: {selectedCategoryName}
                    </p>
                  </div>

                  <div
                    className={`pform-review__card${
                      formData.hasUrgency ? " is-urgent" : ""
                    }`}
                  >
                    <span className="pform-review__label">
                      <FiClock size={13} /> Urgência
                    </span>
                    <p
                      className="pform-review__value"
                      style={formData.hasUrgency ? { color: "#c2410c" } : undefined}
                    >
                      {formData.hasUrgency ? "Validade próxima" : "Lote padrão"}
                    </p>
                    <p className="pform-review__note">
                      {formData.hasUrgency
                        ? "Com selo de alerta na vitrine."
                        : "Sem selo de alerta."}
                    </p>
                  </div>

                  <div className="pform-review__card">
                    <span className="pform-review__label">
                      <FiMapPin size={13} /> Abrangência
                    </span>
                    <p className="pform-review__value">
                      {formData.coverageStates.length === 0
                        ? "Brasil inteiro"
                        : `${formData.coverageStates.length} estado${
                            formData.coverageStates.length === 1 ? "" : "s"
                          }`}
                    </p>
                    <p className="pform-review__note">
                      {formData.coverageCities.length > 0
                        ? `${formData.coverageCities.length} cidade(s) específicas`
                        : "Sem restrição de cidade."}
                    </p>
                  </div>
                </div>

                {formData.coverageStates.length > 0 && (
                  <div className="pform-block">
                    <h4 className="pform-block__title">
                      <FiMapPin size={15} /> Onde este produto aparece
                    </h4>
                    <div style={{ marginTop: 6 }}>
                      {formData.coverageStates.map((sigla) => {
                        const stateInfo = ibgeStates.find((item) => item.sigla === sigla);
                        const cities = formData.coverageCities.filter((city) =>
                          (ibgeCitiesCache[sigla] || []).some(
                            (item) => item.nome === city
                          )
                        );

                        return (
                          <div key={sigla} className="pform-review__state">
                            <strong>{stateInfo?.nome || sigla}</strong>
                            {cities.length > 0 ? (
                              <div className="pform-chips">
                                {cities.map((city) => (
                                  <span key={city} className="pform-chip">
                                    {city}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="pform-field__hint">Todo o estado</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {!formData.media.length && (
                  <div className="pform-alert">
                    <FiImage size={17} style={{ flexShrink: 0, marginTop: 1 }} />
                    <span>
                      Este produto vai para a fila <strong>sem nenhuma foto</strong>.
                      Anúncios sem imagem costumam ser reprovados — volte ao passo 1 e
                      adicione ao menos a capa.
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Navegação do wizard */}
        <div className="wizard-footer">
          {step > 1 ? (
            <button
              type="button"
              className="button button--ghost"
              onClick={prevStep}
              style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}
            >
              <FiChevronLeft size={18} /> Voltar
            </button>
          ) : (
            <button
              type="button"
              className="button button--ghost"
              onClick={() => navigate(-1)}
              style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}
            >
              <FiChevronLeft size={18} /> Cancelar
            </button>
          )}

          <span className="wizard-footer__spacer" />

          {step === 1 && !isStep1Valid && (
            <span className="pform-field__hint" style={{ textAlign: "right" }}>
              Preencha título e categoria para avançar.
            </span>
          )}

          {step < 3 ? (
            <button
              type="button"
              className="button button--primary"
              onClick={nextStep}
              disabled={!isStep1Valid && step === 1}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.8rem 1.8rem",
                opacity: !isStep1Valid && step === 1 ? 0.5 : 1,
                cursor: !isStep1Valid && step === 1 ? "not-allowed" : "pointer",
              }}
            >
              Avançar <FiChevronRight size={18} />
            </button>
          ) : (
            <button
              type="button"
              className="button button--primary fade-in"
              onClick={handleSubmit}
              disabled={loading}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.6rem",
                padding: "0.8rem 1.8rem",
                minWidth: "210px",
                opacity: loading ? 0.7 : 1,
                cursor: loading ? "not-allowed" : "pointer",
              }}
            >
              <FiSave size={18} />
              {loading
                ? "Enviando..."
                : isEditing
                  ? "Salvar alterações"
                  : "Enviar para aprovação"}
            </button>
          )}
        </div>
      </div>

      <ImageCropModal
        isOpen={Boolean(cropSource)}
        imageSrc={cropSource?.dataUrl}
        fileName={cropSource?.name}
        mimeType={cropSource?.type}
        busy={uploading}
        onCancel={closeCropper}
        onConfirm={confirmCrop}
        onUseOriginal={useOriginalPhoto}
      />
    </div>
  );
};

export default NewProductPage;
